/**
 * AppHeader — Polished header with theme support.
 * Styles: flat (default), gradient, minimal.
 */
import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box, Button, Typography, useTheme,
} from '@mui/material';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { getColorsFromConfig } from './AppThemeProvider';

export default function AppHeader({ config }) {
  const theme = useTheme();
  const t = config?.theme || {};
  const headerStyle = t.headerStyle || 'flat';
  const logoUrl = t.logoUrl;
  const citizenMode = Boolean(
    config?.citizenMode || config?.metadata?.interviewFlow?.citizenMode,
  );
  const name = config?.name || 'App';
  const helpPath = citizenMode
    ? null
    : (config?.metadata?.interviewFlow?.helpPath
      || config?.metadata?.helpPath
      || null);
  const colors = getColorsFromConfig(t);
  const isDark = theme.palette.mode === 'dark';

  if (citizenMode) {
    // CitizenAdvisorShell owns city branding + step chrome.
    return null;
  }

  const gradientBg = `linear-gradient(135deg, ${colors.primary} 0%, ${colors.accent} 50%, ${colors.secondary || colors.primary} 100%)`;

  const baseSx = {
    flex: '0 0 auto',
    display: 'flex',
    alignItems: 'center',
    gap: 2,
    px: { xs: 2, sm: 3 },
    py: 1.5,
  };

  const styleSx = {
    flat: {
      ...baseSx,
      borderBottom: 1,
      borderColor: 'divider',
      bgcolor: 'background.paper',
    },
    gradient: {
      ...baseSx,
      background: gradientBg,
      color: 'white',
      boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
      '& .MuiTypography-root': { color: 'white' },
    },
    minimal: {
      ...baseSx,
      bgcolor: isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.02)',
      borderBottom: 1,
      borderColor: 'divider',
    },
  };

  return (
    <Box sx={{ ...(styleSx[headerStyle] || styleSx.flat), justifyContent: 'space-between' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, minWidth: 0 }}>
        {logoUrl && (
          <Box
            component="img"
            src={logoUrl}
            alt=""
            sx={{
              height: 36,
              maxWidth: 120,
              objectFit: 'contain',
              filter: headerStyle === 'gradient' ? 'brightness(0) invert(1)' : undefined,
            }}
            onError={(e) => { e.target.style.display = 'none'; }}
          />
        )}
        <Typography variant="h6" sx={{ fontWeight: 600, letterSpacing: '-0.02em' }} noWrap>
          {name}
        </Typography>
      </Box>
      {helpPath && (
        <Button
          component={RouterLink}
          to={helpPath}
          size="small"
          startIcon={<HelpOutlineIcon fontSize="small" />}
          sx={{
            textTransform: 'none',
            flexShrink: 0,
            color: headerStyle === 'gradient' ? 'inherit' : 'primary.main',
          }}
        >
          Help
        </Button>
      )}
    </Box>
  );
}
